import { memo, type ReactNode } from 'react'
import ReactMarkdown, { type Components } from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { cn } from '@/utils/cn'

export interface MarkdownProps {
  children: string | null | undefined
  className?: string
  /** Render as inline content (no block wrappers, no extra spacing). */
  inline?: boolean
}

const blockComponents: Components = {
  p: ({ children }) => (
    <p className="my-2 first:mt-0 last:mb-0 leading-relaxed whitespace-pre-wrap">
      {children}
    </p>
  ),
  h1: ({ children }) => (
    <h1 className="text-lg font-semibold mt-4 mb-2 first:mt-0">{children}</h1>
  ),
  h2: ({ children }) => (
    <h2 className="text-base font-semibold mt-4 mb-2 first:mt-0">{children}</h2>
  ),
  h3: ({ children }) => (
    <h3 className="text-sm font-semibold mt-3 mb-1.5 first:mt-0">{children}</h3>
  ),
  h4: ({ children }) => (
    <h4 className="text-sm font-semibold mt-3 mb-1 first:mt-0">{children}</h4>
  ),
  ul: ({ children }) => (
    <ul className="my-2 ml-5 list-disc space-y-1">{children}</ul>
  ),
  ol: ({ children }) => (
    <ol className="my-2 ml-5 list-decimal space-y-1">{children}</ol>
  ),
  li: ({ children }) => <li className="leading-relaxed">{children}</li>,
  blockquote: ({ children }) => (
    <blockquote className="my-2 pl-3 border-l-2 border-slate-300 dark:border-slate-600 italic text-slate-600 dark:text-slate-300">
      {children}
    </blockquote>
  ),
  code: ({ className, children, ...rest }) => {
    const isBlock = /language-/.test(className ?? '')
    if (isBlock) {
      return (
        <code
          className={cn(
            'block w-full overflow-x-auto rounded-md bg-slate-100 dark:bg-slate-800 px-3 py-2 font-mono text-xs',
            className,
          )}
          {...rest}
        >
          {children}
        </code>
      )
    }
    return (
      <code
        className="rounded bg-slate-100 dark:bg-slate-800 px-1 py-0.5 font-mono text-xs"
        {...rest}
      >
        {children}
      </code>
    )
  },
  pre: ({ children }) => (
    <pre className="my-2 overflow-x-auto rounded-md bg-slate-100 dark:bg-slate-800 p-3 text-xs leading-snug">
      {children}
    </pre>
  ),
  a: ({ children, href }) => (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="text-black dark:text-white underline underline-offset-2 decoration-slate-400 dark:decoration-slate-500 hover:opacity-80"
    >
      {children}
    </a>
  ),
  strong: ({ children }) => (
    <strong className="font-semibold">{children}</strong>
  ),
  em: ({ children }) => <em className="italic">{children}</em>,
  hr: () => <hr className="my-3 border-slate-200 dark:border-slate-700" />,
  table: ({ children }) => (
    <div className="my-3 w-full overflow-x-auto">
      <table className="min-w-full border-collapse text-xs">{children}</table>
    </div>
  ),
  thead: ({ children }) => (
    <thead className="bg-slate-100 dark:bg-slate-800">{children}</thead>
  ),
  th: ({ children, style }) => (
    <th
      style={style}
      className="border border-slate-300 dark:border-slate-700 px-2 py-1 text-left font-semibold"
    >
      {children}
    </th>
  ),
  td: ({ children, style }) => (
    <td
      style={style}
      className="border border-slate-200 dark:border-slate-700 px-2 py-1 align-top"
    >
      {children}
    </td>
  ),
}

// Inline mode: collapse paragraphs to spans so Markdown can be embedded
// inside an existing line of text (e.g. cleaner-diff `reason` field).
const inlineComponents: Components = {
  ...blockComponents,
  p: ({ children }) => <span>{children}</span>,
}

const MarkdownInner = ({ children, className, inline = false }: MarkdownProps) => {
  if (children == null || children === '') return null
  return (
    <div className={cn(inline ? 'inline' : 'block', className)}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={inline ? inlineComponents : blockComponents}
      >
        {children}
      </ReactMarkdown>
    </div>
  )
}

export const Markdown = memo(MarkdownInner) as (props: MarkdownProps) => ReactNode

export default Markdown
